import {z} from "zod";

// Zod allows us to ensure that the response type meets our expectations, at runtime.
// This is better than asserting that the response type is of a certain type, because
// if the response type changes, the code will break at runtime, rather than silently.
// This gives us a better developer experience, and allows us to catch bugs earlier.

const LessonCardSchema = z.object({
  type: z.literal("lesson"),
  lessonId: z.number(),
  title: z.string(),
  artistName: z.string(),
  hasVideo: z.boolean(),
  imageUrl: z.string(),
  levelDifficulty: z.string(),
  difficultyIdentifier: z.union([z.literal("rookie"), z.literal("intermediate"), z.literal("advanced")]),
});

const SongCardSchema = z.object({
  type: z.literal("song"),
  songId: z.number(),
  title: z.string(),
  artistName: z.string(),
  hasVideo: z.boolean(),
  imageUrl: z.string(),
  numberOfArrangements: z.number(),
});

// A simple union does not work here. We need to use a discriminated union.
// This approach to discriminated unions is the current approach by the Zod team,
// but an alternative approach is being considered.
// See https://github.com/colinhacks/zod/issues/2106
const PlaylistCardSchema = z.discriminatedUnion('type', [LessonCardSchema, SongCardSchema]);

export const PlaylistSchema = z.object({
  id: z.number(),
  title: z.string(),
  cards: z.array(PlaylistCardSchema),
});

export type PlaylistResponse = z.infer<typeof PlaylistSchema>;
export type PlaylistCard = z.infer<typeof PlaylistCardSchema>;
export type PlaylistLessonCard = z.infer<typeof LessonCardSchema>;
export type PlaylistSongCard = z.infer<typeof SongCardSchema>;
